import { type IEvent } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type FeatureStandard } from '@solinkcorp/event-standard/lib/src/types';
import { useTimeout } from '@solinkcorp/solink-components';
import * as EventClient from 'clients/EventClient';
import findLastIndex from 'lodash/findLastIndex';
import isEqual from 'lodash/isEqual';
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_EVENT_FITLERS } from 'stores/EventFilterStore';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import * as EventUtil from 'utils/EventUtil';
import LoggingUtil from 'utils/LoggingUtil';
import { SCHEDULED_FEATURE, WEB_QUERY_COUNT } from 'vd-constants';
import { useCurrentFeature } from './useEventFeature';
import { useMemoShallowEquals } from './useMemoShallowEquals';

/**
 * When we try to load recent events and there aren't any, this is how long the
 * `moreRecentEventsAvailable` flag will be false.
 */
const MORE_RECENT_EVENTS_AVAILABLE_TIMEOUT = 15000;

const DEFAULT_EVENT_PARAMETERS = { eventFilterState: DEFAULT_EVENT_FITLERS };

/**
 * Returns the cursor only if every slot is populated. `created` should be set
 * on every event, but defend against null slots anyway - a null in the cursor
 * tells search_after to resume at "null" and produces unreliable pagination.
 * Treat null-containing cursors as absent so the caller falls back to the
 * time-window pagination path.
 */
function usableCursor(next: unknown[] | undefined): unknown[] | undefined {
  if (!Array.isArray(next) || next.length === 0) return undefined;
  return next.every((value) => value !== null && value !== undefined) ? next : undefined;
}

/** Data about events we have fetched from the server. */
interface EventData {
  /** Parameters used to fetch these events. */
  parameters: EventClient.EventClientQueryParameters;
  /** Events we have from the server, in reverse sorted order. */
  events: readonly IEvent[];
  /**
   * The `rangeStart` is the start of the time period that is covered by `events`.
   * Note that the earliest event in `events` may be later than `rangeStart` -
   * if we try to fetch events within 1 day of a certain time, and there are no
   * events before that time, then we know we have "all the events" between the
   * time we tried to fetch and one day previous, so rangeStart will be one day
   * previous.
   *
   * If the date we're trying to fetch events for is outside of [rangeStart, rangeEnd]
   * then we know we need to throw away the events we have and fetch fresh events.
   */
  rangeStart: number;
  /**
   * Same as rangeStart, but the end of the range.
   */
  rangeEnd: number;
  /**
   * True if there are (possibly) more events to fetch before `rangeStart`.
   */
  earlierEventsAvailable: boolean;
  /**
   * True if there are (possibly) more events to after before `rangeEnd`.
   */
  moreRecentEventsAvailable: boolean;
  /**
   * This is the time we actually tried to fetch.  We keep track of this because
   * there's a weird corner case here - if we try to fetch a date in the future,
   * it will always be outside of [rangeStart, rangeEnd], which leads to an
   * unfortunate infinite loop.  :(  So if we've fetched some events, and the
   * time we last fetched is IDENTICAL to the time we are currently looking at,
   * then there's no sense trying to fetch events again - we're just going to get
   * the same thing back.
   */
  fetchTime: number;
  /**
   * Total number of events available for the given parameters.
   */
  total?: number;
  /**
   * Date of the next events to fetch. If there are multiple sorts, this will be an array of the sort field values for the next events.
   */
  next?: unknown[];
  /**
   * Platform `next` [startTime, created] cursor for fetching events older than
   * the oldest currently loaded event. Funnel through `usableCursor` before
   * sending it back so a null slot triggers the time-window fallback.
   */
  nextEarlier?: unknown[];
  /**
   * Platform `next` [startTime, created] cursor for fetching events newer than
   * the most recent loaded event. Symmetric counterpart to `nextEarlier`.
   */
  nextMoreRecent?: unknown[];
}

type InternalEventState =
  | { eventsLoading: boolean; error?: Error; eventData?: undefined }
  | { eventsLoading: boolean; error?: undefined; eventData: EventData };

export type DateQuery =
  | { centeredAround: Date | number }
  | {
      start: number;
      end: number;
    };

function isCenteredAround(dateQuery: DateQuery): dateQuery is { centeredAround: Date | number } {
  return 'centeredAround' in dateQuery;
}

export interface EventController extends Partial<Omit<EventData, 'fetchTime'>> {
  /** Parameters used to fetch these events. */
  parameters: EventClient.EventClientQueryParameters;
  // Optional description of the events in this controller.
  description?: string;
  /**
   * True if events are currently being loaded.  If we're fetching more or earlier
   * events, this will be true and `events` will be defined.  If we're loading
   * a brand new set of events (e.g. the interval changes) then this will be true
   * and `events` will be undefined.
   */
  eventsLoading: boolean;
  /**
   * If there was an error loading events, this will be the error.
   */
  eventsError?: undefined | Error;
  /** True if there are more events to be fetched before the start of `events`. */
  earlierEventsAvailable: boolean;
  /**
   * True if there might be more events to be fetched after the end of `events`.
   * We set this false temporarily if we try to fetch events near "now" and there
   * aren't any.
   */
  moreRecentEventsAvailable: boolean;
  /**
   * Call to fetch more recent events and add them to the end of the `events` array.
   */
  fetchMoreRecentEvents: () => void;
  /**
   * Call to fetch earlier events and add them to the start of the `events` array.
   */
  fetchEarlierEvents: () => void;
  /**
   * Call to all events centered around use selected date.
   */
  fetchEventsCenteredAround?: () => void;
  /**
  /**
   * Call to refetch events in the current date range.
   */
  fetchEventsInRange?: () => void;
  /**
   * Call when we modify an event client side (e.g. when we save an event to
   * CloudVMS and want to insert the new cloud videos) and don't want to
   * reload all events.  Note that this *only* modifies the client-side event.
   */
  updateEvent: (newEvent: IEvent) => void;
  /**
   * Call when we add an event client side (e.g. when we create a bookmark event or scheduled event and don't want to
   * reload all events.  Note that this *only* modifies the client-side event.
   */
  addEvent?: (newEvent: IEvent) => void;
  /**
   * Call when we delete an event client side (e.g. when we archive an event and platform can take up to 30 seconds to update)
   * Note that this *only* modifies the client-side event.
   */
  removeEvent?: (targetEvent: IEvent) => void;
}

type SortOrder = { [key: string]: { order: 'desc' | 'asc' } }[];

export interface FetchOptions {
  description?: string;
  offset?: number;
  count?: number;
  sortOrder?: SortOrder;
  next?: unknown[];
  limitTotal?: boolean;
}

export function useSolinkEvents(
  params: EventClient.EventClientQueryParameters,
  dateQuery: DateQuery,
  options?: FetchOptions,
): EventController;

export function useSolinkEvents(
  params: undefined,
  dateQuery: DateQuery,
  options?: FetchOptions,
): undefined;

export function useSolinkEvents(
  params: EventClient.EventClientQueryParameters | undefined,
  dateQuery: DateQuery,
  options?: FetchOptions,
): EventController | undefined;

/**
 * Fetch events from the server.
 *
 * This returns an array of events which match the query parameters and which
 * overlap the specified time.  If the time changes to another time within the
 * interval covered by the currently loaded events, then the list of events will
 * not change.  If the time changes to a range outside the currently loaded interval,
 * this will return `undefined` for the event list while new events are loaded.
 *
 * The "currently loaded interval" is based on what events we have actually
 * fetched, and what events we have tried and failed to fetch.  For example,
 * if we try to fetch events below a given end time, and we successfully
 * fetch events, then we know the range of events we "have" goes all the way
 * to the start of the first event we retrieved.  If we don't get back any events,
 * then we know our interval covers all the way to the start time we requested.
 */
export function useSolinkEvents(
  params: EventClient.EventClientQueryParameters | undefined,
  dateQuery: DateQuery,
  options: FetchOptions = {},
): EventController | undefined {
  const [eventsState, setEventsState] = useState<InternalEventState>({
    eventsLoading: false,
  });

  const feature = useCurrentFeature();
  const featureStandard = useFeatureStandard();
  const isInFuture = feature === SCHEDULED_FEATURE;

  // Keep track of which "fetch count" we're on right now, so we can ignore results
  // from earlier stale fetches if params change.
  const fetchId = useRef(0);

  // Parameters used to last fetch events currently in `eventsState` (or currently being fetched if `loading` is true).
  const fetchedParamsRef = useRef<EventClient.EventClientQueryParameters | undefined>();

  // Date used to last fetch events.
  const fetchedDateQueryRef = useRef<DateQuery | undefined>();

  // Fetch options
  const fetchedOptions = useRef<FetchOptions | undefined>();

  // Do the initial fetch if there are no events, or if the `parameters` have changed
  // or the `date` has changed and is outside the time window we have data for.
  // TODO: We hit this effect WAY too much.  What's going on?
  useEffect(() => {
    const { eventData } = eventsState;

    const prevParameters = fetchedParamsRef.current;
    const prevDateQuery = fetchedDateQueryRef.current;
    const prevOptions = fetchedOptions.current;
    const needFetch = needToFetchNewEvents(
      params,
      prevParameters,
      dateQuery,
      prevDateQuery,
      eventData,
      options,
      prevOptions,
    );

    if (needFetch && params) {
      // This ensures that when events are fetch from inside this useEffect it doesn't get batched
      // with other calls to functions that fetch events by adding it to the microtask queue.
      // Code in the microtask queue will always be executed before code in the callback queue.
      queueMicrotask(() => {
        fetchedParamsRef.current = parametersExtension(params, featureStandard, feature);
        fetchedDateQueryRef.current = dateQuery;
        fetchedOptions.current = options;

        if (isCenteredAround(dateQuery)) {
          doFetchEventsCenteredAround(
            fetchId,
            fetchedParamsRef.current,
            dateQuery.centeredAround,
            setEventsState,
            isInFuture,
          );
        } else {
          doFetchEventsInRange(
            fetchId,
            fetchedParamsRef.current,
            dateQuery,
            setEventsState,
            options,
          );
        }
      });
    }
  }, [dateQuery, params, eventsState, isInFuture, featureStandard, feature, options]);

  const fetchEventsCenteredAround = useCallback(() => {
    const eventParams = fetchedParamsRef.current;
    if (isCenteredAround(dateQuery) && eventParams) {
      doFetchEventsCenteredAround(fetchId, eventParams, dateQuery.centeredAround, setEventsState);
    }
  }, [dateQuery]);

  const fetchEventsInRange = useCallback(() => {
    const eventParams = fetchedParamsRef.current;
    if (eventParams && !isCenteredAround(dateQuery)) {
      doFetchEventsInRange(fetchId, eventParams, dateQuery, setEventsState, options);
    }
  }, [dateQuery, options]);

  const fetchMoreRecentEvents = useCallback(() => {
    const eventParams = fetchedParamsRef.current;
    doFetchMoreRecentEvents(
      fetchId,
      eventParams,
      eventsState,
      fetchedDateQueryRef.current,
      setEventsState,
      isInFuture,
    );
  }, [eventsState, isInFuture]);

  const fetchEarlierEvents = useCallback(() => {
    const eventParams = fetchedParamsRef.current;
    doFetchEarlierEvents(
      fetchId,
      eventParams,
      eventsState,
      fetchedDateQueryRef.current,
      setEventsState,
    );
  }, [eventsState]);

  const updateEvent = useCallback((update: IEvent) => {
    setEventsState((eventsState) => {
      let result = eventsState;

      const { eventData } = eventsState;
      if (eventData) {
        const { events } = eventData;
        const updatedEvents = EventUtil.updateEventInArray(events, update);

        if (updatedEvents !== events) {
          result = {
            eventsLoading: eventsState.eventsLoading,
            error: undefined,
            eventData: {
              ...eventData,
              events: updatedEvents,
            },
          };
        }
      }

      return result;
    });
  }, []);

  const addEvent = useCallback((newEvent: IEvent) => {
    setEventsState((eventsState) => {
      let result = eventsState;

      const { eventData } = eventsState;
      if (eventData) {
        const { events } = eventData;
        const updatedEvents = EventUtil.addEventToArray(events, newEvent);
        if (updatedEvents !== events) {
          result = {
            eventsLoading: eventsState.eventsLoading,
            error: undefined,
            eventData: {
              ...eventData,
              events: updatedEvents,
            },
          };
        }
      }

      return result;
    });
  }, []);

  const removeEvent = useCallback((targetEvent: IEvent) => {
    setEventsState((eventsState) => {
      let result = eventsState;

      const { eventData } = eventsState;
      if (eventData) {
        const { events } = eventData;
        const updatedEvents = EventUtil.removeEventFromArray(events, targetEvent);
        if (updatedEvents !== events) {
          result = {
            eventsLoading: eventsState.eventsLoading,
            error: undefined,
            eventData: {
              ...eventData,
              events: updatedEvents,
            },
          };
        }
      }

      return result;
    });
  }, []);

  // If `moreRecentEventsAvailable` is false, then set a timeout to set it back
  // to true in a little bit.
  const checkMoreRecentEventsTimeout =
    eventsState.eventData && !eventsState.eventData?.moreRecentEventsAvailable
      ? MORE_RECENT_EVENTS_AVAILABLE_TIMEOUT
      : undefined;
  useTimeout(
    () => setMoreRecentEventsAvailableTrue(eventsState, setEventsState),
    checkMoreRecentEventsTimeout,
    [eventsState],
  );

  const eventController = useMemoShallowEquals({
    description: options?.description,
    eventsLoading: eventsState.eventsLoading,
    eventsError: eventsState.error,
    fetchMoreRecentEvents,
    fetchEarlierEvents,
    fetchEventsCenteredAround,
    fetchEventsInRange,
    addEvent,
    removeEvent,
    updateEvent,
    ...(eventsState.eventData ?? {
      earlierEventsAvailable: false,
      moreRecentEventsAvailable: false,
    }),
    parameters: eventsState.eventData?.parameters || params || DEFAULT_EVENT_PARAMETERS,
  });

  return params ? eventController : undefined;
}

/**
 * Extend query parameters based on feature standard and currently selected feature.
 *
 * This notably honors the `mergeWith` field in the feature standard, which allows
 * events from another subtype to be included in the response for a given event subtype tab
 *
 * @param params events query parameter
 * @param featureStandard feature standard declaration
 * @param feature currently selected feature tab, if there is any
 * @returns extended query parameters
 */
function parametersExtension(
  params: EventClient.EventClientQueryParameters,
  featureStandard: FeatureStandard | undefined,
  feature: string | undefined,
): EventClient.EventClientQueryParameters {
  if (!feature || !featureStandard) {
    return params;
  }

  const featureData = featureStandard.tabs[feature];
  if (!featureData) {
    return params;
  }

  const featureSubtypes = featureData.eventSubtypes;
  if (!featureSubtypes) {
    return params;
  }

  const subtypeFilter = params?.eventSubtypeFilter;
  if (!subtypeFilter) {
    return params;
  }
  const enabledSubtypes = Object.keys(subtypeFilter).filter((subtype) => !!subtypeFilter[subtype]);
  const subtypesToUse = enabledSubtypes.concat(
    Object.keys(subtypeFilter).filter((subtype) =>
      enabledSubtypes.some((enabledSubtype) => subtype.includes(enabledSubtype)),
    ),
  );

  for (const subtype of subtypesToUse) {
    const mergeWith = featureSubtypes[subtype]?.mergeWith || [];
    for (const mergeSubtype of mergeWith) {
      if (subtypeFilter[mergeSubtype] === undefined) {
        subtypeFilter[mergeSubtype] = true;
      }
    }
  }

  return {
    ...params,
    eventSubtypeFilter: subtypeFilter,
  };
}

/** Allocate a new `fetchId` from the fetchId ref. */
function getNewFetchId(fetchId: MutableRefObject<number>) {
  if (fetchId.current <= Number.MAX_SAFE_INTEGER) {
    fetchId.current++;
  } else {
    fetchId.current = 0;
  }
  return fetchId.current;
}

/**
 * Returns true if the parameters have changed, or the date is outside the range
 * of events we currently have fetched.
 */
function needToFetchNewEvents(
  parameters: EventClient.EventClientQueryParameters | undefined,
  fetchedParameters: EventClient.EventClientQueryParameters | undefined,
  dateQuery: DateQuery,
  prevDateQuery: DateQuery | undefined,
  eventData?: EventData,
  options?: FetchOptions,
  prevOptions?: FetchOptions,
) {
  // We're "in the fetched range" if the time is between rangeStart and rangeEnd,
  // or if the time hasn't changed since the last time we fetched (this second case
  // can happen when we try to set `date` in the future).
  let inFetchedRange = false;

  if (isEqual(dateQuery, prevDateQuery)) {
    inFetchedRange = true;
  } else if (
    eventData &&
    isCenteredAround(dateQuery) &&
    eventData.rangeStart <= +dateQuery.centeredAround &&
    eventData.rangeEnd >= +dateQuery.centeredAround
  ) {
    inFetchedRange = true;
  } else if (
    eventData &&
    !isCenteredAround(dateQuery) &&
    eventData.rangeStart >= dateQuery.start &&
    eventData.rangeEnd <= dateQuery.end
  ) {
    // We are only inFetchedRange for a { start, end } query if all data is between start and end.
    inFetchedRange = true;
  }

  let needFetch = false;
  if (eventData && !inFetchedRange) {
    LoggingUtil._debug(
      `Fetching new events: ${JSON.stringify(dateQuery)} is outside ` +
        `[${new Date(eventData.rangeStart)}, ${new Date(eventData.rangeEnd)}]`,
    );
    needFetch = true;
  } else if (!eventData && !inFetchedRange) {
    LoggingUtil._debug(`Fetching new events: Date has changed and we have no events.`);
    needFetch = true;
  } else if (!isEqual(parameters, fetchedParameters)) {
    LoggingUtil._debug(`Fetching new events: Parameters changed.`);
    needFetch = true;
  } else if (!isEqual(options, prevOptions)) {
    LoggingUtil._debug(`Fetching new events: Options changed.`);
    needFetch = true;
  }

  return parameters !== undefined && needFetch;
}

/**
 * Fetch a brand new set of events, centered around the given date.
 */
function doFetchEventsCenteredAround(
  fetchId: MutableRefObject<number>,
  parameters: EventClient.EventClientQueryParameters,
  date: number | Date,
  setEventsState: (state: InternalEventState) => void,
  isInFuture: boolean = false,
) {
  const currentFetchId = getNewFetchId(fetchId);
  setEventsState({ eventsLoading: true });

  EventClient.fetchEventsCenteredAround(parameters, date, isInFuture)
    .then((result) => {
      if (currentFetchId === fetchId.current) {
        setEventsState({
          eventsLoading: false,
          eventData: {
            parameters,
            ...result,
            fetchTime: date.valueOf(),
            moreRecentEventsAvailable: true,
          },
        });
      }
    })
    .catch((error) => {
      if (currentFetchId === fetchId.current) {
        console.error(`Error fetching events:`, error);
        setEventsState({ eventsLoading: false, error });
      }
    });
}

/**
 * Fetch a brand new set of events, in the given date range.
 */
function doFetchEventsInRange(
  fetchId: MutableRefObject<number>,
  parameters: EventClient.EventClientQueryParameters,
  dateRange: { start: number; end: number },
  setEventsState: (state: InternalEventState) => void,
  options: FetchOptions,
) {
  const currentFetchId = getNewFetchId(fetchId);
  setEventsState({ eventsLoading: true });

  EventClient.fetchEventsBetween(parameters, dateRange, {
    sortOrder: options.sortOrder ?? 'desc',
    count: options.count ?? WEB_QUERY_COUNT,
    offset: options.offset ?? 0,
    next: options.next ?? undefined,
    totalLimit: options.limitTotal ?? undefined,
  })
    .then((result) => {
      const earlierEventsAvailable = result.events.length >= WEB_QUERY_COUNT;

      if (currentFetchId === fetchId.current) {
        setEventsState({
          eventsLoading: false,
          eventData: {
            ...result,
            parameters,
            fetchTime: dateRange.end,
            earlierEventsAvailable,
            moreRecentEventsAvailable: false,
            nextEarlier: result.next,
          },
        });
      }
    })
    .catch((error) => {
      if (currentFetchId === fetchId.current) {
        console.error(`Error fetching events:`, error);
        setEventsState({ eventsLoading: false, error });
      }
    });
}

function doFetchMoreRecentEvents(
  fetchId: MutableRefObject<number>,
  parameters: EventClient.EventClientQueryParameters | undefined,
  eventsState: InternalEventState,
  dateQuery: DateQuery | undefined,
  setEventsState: (state: InternalEventState) => void,
  isInFuture: boolean = false,
) {
  const { eventData } = eventsState;
  if (!parameters || !eventData || !dateQuery || eventsState.error || eventsState.eventsLoading) {
    LoggingUtil._warn(`Can't fetch more recent events right now...`);
    return;
  }

  if (!isCenteredAround(dateQuery) && eventData.rangeStart <= dateQuery.start) {
    // Nothing to fetch!
    return;
  }

  const currentFetchId = getNewFetchId(fetchId);
  setEventsState({
    eventsLoading: true,
    eventData,
  });

  const isHandlingFutureEvents = isInFuture && eventData.rangeEnd > Date.now();
  const cursor = usableCursor(eventData.nextMoreRecent);
  // With a usable cursor, Platform handles the boundary via search_after so we
  // anchor exactly at rangeEnd. Without one, fall back to the legacy
  // `rangeEnd + 1s` buffer so we don't re-fetch the most recent loaded event.
  const requestStartTime = isHandlingFutureEvents
    ? Date.now()
    : cursor
      ? eventData.rangeEnd
      : eventData.rangeEnd + 1000;

  EventClient.fetchMoreRecentEvents({ ...parameters }, requestStartTime, isInFuture, {
    next: cursor,
  })
    .then((results) => {
      if (currentFetchId === fetchId.current) {
        const moreRecentEvents = results.events.reverse();

        let noMoreEvents;
        if (!isInFuture) {
          noMoreEvents =
            results.events.length === 0 &&
            results.rangeEnd >= Date.now() - MORE_RECENT_EVENTS_AVAILABLE_TIMEOUT;
        } else {
          noMoreEvents = false;
        }

        let updatedEvents = moreRecentEvents.concat(eventData.events);
        const indexOfFirstFutureEventFromMoreRecent = findLastIndex(
          moreRecentEvents,
          (event) => new Date(event.startTime).getTime() >= Date.now(),
        );
        if (isInFuture && indexOfFirstFutureEventFromMoreRecent >= 0) {
          const currentEvents = eventData.events;
          const indexOfFirstFutureEvent = findLastIndex(
            currentEvents,
            (event) => new Date(event.startTime).getTime() >= Date.now(),
          );
          const eventsInPast = currentEvents.slice(
            indexOfFirstFutureEvent + 1,
            currentEvents.length,
          );
          const eventsInFutureFromMoreRecentEvents = moreRecentEvents.slice(
            0,
            indexOfFirstFutureEventFromMoreRecent + 1,
          );
          updatedEvents = eventsInFutureFromMoreRecentEvents.concat(eventsInPast);
        }

        setEventsState({
          eventsLoading: false,
          eventData: {
            parameters,
            events: updatedEvents,
            rangeStart: eventData.rangeStart,
            rangeEnd: results.rangeEnd,
            earlierEventsAvailable: eventData.earlierEventsAvailable,
            moreRecentEventsAvailable: !noMoreEvents,
            fetchTime: eventData.fetchTime,
            nextEarlier: eventData.nextEarlier,
            nextMoreRecent: results.next,
          },
        });
      }
    })
    .catch((error) => {
      if (currentFetchId === fetchId.current) {
        setEventsState({ eventsLoading: false, error });
      }
    });
}

function doFetchEarlierEvents(
  fetchId: MutableRefObject<number>,
  parameters: EventClient.EventClientQueryParameters | undefined,
  eventsState: InternalEventState,
  dateQuery: DateQuery | undefined,
  setEventsState: (state: InternalEventState) => void,
) {
  const { eventData } = eventsState;
  if (!parameters || !eventData || !dateQuery || eventsState.error || eventsState.eventsLoading) {
    LoggingUtil._warn(`Can't fetch earlier events right now...`);
    return;
  }

  const {
    events: existingEvents,
    rangeEnd,
    rangeStart,
    moreRecentEventsAvailable,
    fetchTime,
    nextEarlier,
    nextMoreRecent,
  } = eventData;

  if (!isCenteredAround(dateQuery) && rangeEnd >= dateQuery.end) {
    // Nothing to fetch!
    return;
  }

  const currentFetchId = getNewFetchId(fetchId);
  setEventsState({
    eventsLoading: true,
    eventData,
  });

  // Anchor the fetch window at the oldest currently-loaded event. If Platform
  // gave us a usable `next` cursor we resume from it; otherwise (e.g. event
  // subtypes whose `id` isn't indexed so the cursor's tiebreaker is null) we
  // fall back to the legacy time-window pagination which steps back from the
  // boundary inside `fetchEarlierEventsHelper`.
  const requestStartTime = isCenteredAround(dateQuery)
    ? rangeStart
    : existingEvents[existingEvents.length - 1].startTime.valueOf();

  EventClient.fetchEarlierEvents(parameters, requestStartTime, {
    startDate: !isCenteredAround(dateQuery) ? dateQuery.start : undefined,
    next: usableCursor(nextEarlier),
  })
    .then(({ events: earlierEvents, rangeStart, moreEventsToRequest, next: newNextEarlier }) => {
      if (currentFetchId === fetchId.current) {
        setEventsState({
          eventsLoading: false,
          eventData: {
            parameters,
            events: existingEvents.concat(earlierEvents),
            rangeStart,
            rangeEnd,
            earlierEventsAvailable: moreEventsToRequest,
            moreRecentEventsAvailable,
            fetchTime,
            nextEarlier: newNextEarlier,
            nextMoreRecent,
          },
        });
      }
    })
    .catch((error) => {
      if (currentFetchId === fetchId.current) {
        setEventsState({ eventsLoading: false, error });
      }
    });
}

function setMoreRecentEventsAvailableTrue(
  eventsState: InternalEventState,
  setEventsState: (state: InternalEventState) => void,
) {
  if (eventsState.eventData) {
    const { eventData } = eventsState;
    setEventsState({
      ...eventsState,
      eventData: {
        ...eventData,
        moreRecentEventsAvailable: true,
      },
    });
  }
}
